import type { FC, ReactNode } from 'react'
import type { ProductCardData } from '@/types'

import Link from 'next/link'
import { Layers, Plus, ShoppingBag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCart } from '@/lib/cart'
import { useI18n } from '@/lib/i18n'
import { percentOff } from '@/lib/format'
import { unitLabel } from '@/constants/units'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/toast'
import { CmsText } from '@/components/store/cms-text'
import { Num, Price } from '@/components/store/price'
import { ProductMedia } from '@/components/store/product-media'

interface ProductCardProps {
    product: ProductCardData
    priority?: boolean
    className?: string
}

export const ProductCard: FC<ProductCardProps> = ({ product, priority, className }): ReactNode => {
    const { tx, txn, locale } = useI18n()
    const { addItem, open } = useCart()
    const { notify } = useToast()
    const { pricing } = product

    const href = `/products/${product.slug}`
    const discount = percentOff(pricing.retail, pricing.retailFinal)
    const unit = unitLabel(product.unit, locale)
    const quickAdd = !product.hasVariants && product.inStock

    const add = (): void => {
        addItem({ productId: product.id, variantId: null, qty: Math.max(1, product.minOrderQty) })
        notify(tx('تمت الإضافة إلى السلة', 'Added to your cart'), 'success', { label: tx('عرض السلة', 'View cart'), onClick: open })
    }

    return (
        <article className={cn('group relative flex flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-navy-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift hover:ring-navy-200', className)}>
            <Link href={href} className='relative block aspect-square overflow-hidden sm:aspect-[5/4.4]' tabIndex={-1} aria-hidden='true'>
                <ProductMedia
                    src={product.image}
                    alt={product.imageAlt}
                    categorySlug={product.category?.slug}
                    priority={priority}
                    className='h-full w-full'
                    imageClassName='transition-transform duration-700 ease-out group-hover:scale-[1.04]'
                />

                <div className='absolute start-3 top-3 flex flex-col items-start gap-1.5'>
                    {discount > 0 && <Badge tone='navy' className='bg-cyan-500 text-navy-950'><span dir='ltr'>-{discount}%</span></Badge>}
                    {!discount && pricing.bulkOffer && <Badge tone='white'>{tx('عرض الكمية', 'Bulk deal')}</Badge>}
                    {!product.inStock && <Badge tone='white' className='text-red-600'>{tx('نفدت الكمية', 'Sold out')}</Badge>}
                    {product.inStock && product.lowStock && <Badge tone='white' className='text-amber-700'>{tx('كمية محدودة', 'Low stock')}</Badge>}
                </div>
            </Link>

            <div className='flex flex-1 flex-col gap-3 p-4 sm:p-5'>
                <div className='flex flex-col gap-1.5'>
                    {product.category && (
                        <CmsText table='categories' id={product.category.id} field={product.category.name} className='text-xs font-medium text-cyan-700' />
                    )}

                    <h3 className='text-[15px] font-semibold leading-snug text-navy-950 sm:text-base'>
                        <Link href={href} className='line-clamp-2 transition-colors after:absolute after:inset-0 after:content-[""] hover:text-cyan-800 focus-visible:outline-none'>
                            <CmsText table='products' id={product.id} field={product.name} />
                        </Link>
                    </h3>
                </div>

                <div className='mt-auto flex flex-col gap-2.5'>
                    <div className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
                        {product.priceFrom && <span className='text-xs text-muted-foreground'>{tx('يبدأ من', 'From')}</span>}
                        <Price value={pricing.retailFinal} className='text-xl font-bold text-navy-950' />
                        {discount > 0 && <Price value={pricing.retail} strike className='text-sm text-navy-400' />}
                        <span className='text-xs text-muted-foreground'>
                            {'/ '}
                            {unit}
                        </span>
                    </div>

                    {pricing.wholesaleFinal !== null && (
                        <div className='flex flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl bg-navy-50/80 px-3 py-2 text-xs text-navy-700'>
                            <Layers className='size-3.5 shrink-0 text-cyan-700' aria-hidden='true' />
                            <span className='font-semibold text-navy-900'>{tx('جملة', 'Wholesale')}</span>
                            <Price value={pricing.wholesaleFinal} className='font-semibold text-navy-950' />
                            <span className='text-navy-500'>
                                {txn('من {n}', 'from {n}', { n: <Num value={pricing.wholesaleMinQty} /> })}
                                {' '}
                                {unitLabel(product.unit, locale, pricing.wholesaleMinQty)}
                            </span>
                            {pricing.wholesaleSavingPct > 0 && (
                                <span className='ms-auto rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800'>
                                    {tx('وفّر', 'Save')}
                                    {' '}
                                    <span dir='ltr'>{pricing.wholesaleSavingPct}%</span>
                                </span>
                            )}
                        </div>
                    )}

                    <div className='relative z-10'>
                        {quickAdd
                            ? (
                                <button
                                    type='button'
                                    onClick={add}
                                    className='inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-navy-900 text-sm font-medium text-white transition-all hover:bg-navy-800 active:scale-[0.98]'
                                >
                                    <Plus className='size-4' aria-hidden='true' />
                                    {tx('أضف للسلة', 'Add to cart')}
                                    <span className='sr-only'>{product.name.v}</span>
                                </button>
                            )
                            : (
                                <Link
                                    href={href}
                                    className={cn(
                                        'inline-flex h-10 w-full items-center justify-center gap-2 rounded-full text-sm font-medium transition-all active:scale-[0.98]',
                                        product.inStock ? 'border border-navy-200 bg-white text-navy-900 hover:border-navy-300 hover:bg-navy-50' : 'bg-navy-50 text-navy-500'
                                    )}
                                >
                                    <ShoppingBag className='size-4' aria-hidden='true' />
                                    {product.inStock ? tx('اختر المقاس أو اللون', 'Choose options') : tx('عرض المنتج', 'View product')}
                                </Link>
                            )}
                    </div>
                </div>
            </div>
        </article>
    )
}

export const ProductGrid: FC<{ products: Array<ProductCardData>, className?: string, priorityCount?: number }> = ({ products, className, priorityCount = 0 }): ReactNode => (
    <div className={cn('grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4', className)}>
        {products.map((product, index) => <ProductCard key={product.id} product={product} priority={index < priorityCount} />)}
    </div>
)
